import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '../../api/axiosConfig';
import { useAuthStore } from '@/Stores/auth-v2-store';
import type { StripeBannerConfigInput } from '@/features/societies/helpers/stripe-banner-state';

/**
 * État Stripe d'une société assignée à l'utilisateur courant, tel que renvoyé
 * par `GET /external-users-v2/:id/societies` (champs ajoutés au DTO du service).
 *
 * Étend `StripeBannerConfigInput` pour pouvoir être passé directement à
 * `deriveStripeBannerState`.
 */
export interface MySocietyStripe extends StripeBannerConfigInput {
  societyId: string;
  societyName: string;
  isPrimary: boolean;
  /** Société facturée par un parent → c'est le parent qui porte le compte Stripe. */
  billedId: string | null;
  /** `PRO_SOCIETY.MONTHLY_PAYMENT` legacy — fallback du helper. */
  monthlyPayment: number | null;
}

/**
 * Liste TOUTES les sociétés assignées à l'utilisateur externe courant avec leur
 * état Stripe (sans filtre d'éligibilité, contrairement à `useAvailableSocieties`).
 *
 * Sert au bandeau Stripe de l'outil-pro pour afficher un bandeau par société à
 * problème quand un vendeur gère plusieurs sociétés. Un seul appel réseau : le
 * back résout déjà la config effective de chaque société.
 *
 * Inactif pour les internals (le multi-société ne les concerne pas) et les
 * users non authentifiés.
 */
export const useMySocietiesStripe = () => {
  const currentUser = useAuthStore((s) => s.currentUser);
  const isExternalUser = currentUser?.type === 'EXTERNAL';
  const userId = currentUser?.id;

  const query = useQuery({
    queryKey: ['mySocietiesStripe', userId],
    queryFn: async () => {
      const response = await axiosInstance.get<MySocietyStripe[]>(
        `/external-users-v2/${userId}/societies`,
      );
      return [...response.data].sort(
        (a, b) => Number(b.isPrimary) - Number(a.isPrimary),
      );
    },
    enabled: isExternalUser && !!userId,
    staleTime: 60_000,
  });

  return {
    societies: query.data ?? [],
    isLoading: query.isLoading,
    /**
     * La requête a rendu son verdict (succès ou échec définitif). À la différence
     * de `!isLoading`, reste faux pour une requête désactivée — où `isLoading`
     * vaut vrai indéfiniment (React Query v4).
     */
    isSettled: query.isSuccess || query.isError,
    isExternalUser,
  };
};
